import type {
  BottomSheetPadding,
  BottomSheetProps,
  BottomSheetScroll,
  BottomSheetSize,
} from './BottomSheet.types';
import { BOTTOM_SHEET_SCREEN_INSET_PX } from './BottomSheet.variants';

/**
 * Pure layout maths for the bottom sheet. Everything here is platform-free on
 * purpose: the native shell is a `.native.tsx` file, and `.native.*` is
 * excluded from shared's typecheck (and invisible to the app's `tsc`, which
 * resolves the `.tsx` sibling). Keeping the arithmetic in a plain `.ts` module
 * means the part most likely to be wrong is the part that IS typechecked and
 * unit-testable — the `.native.tsx` glue stays thin enough to eyeball.
 *
 * Ported from flashka-app's pre-DS sheet system (`components/ui/BottomSheet/
 * spacing.ts` + `ScrollContainer.ts` + the sizing block of
 * `BottomSheetContent.tsx`), whose values are load-bearing: they were tuned
 * against real devices. Don't "simplify" them without checking a sheet with a
 * footer, a keyboard and a home indicator on both platforms.
 */

/**
 * Horizontal padding of the sheet body when `padding` is `'default'`.
 *
 * Figma's "Content" frame sits at the card's 12px inset with the Media Slot
 * flush inside it (5593:19466 x=12 w=356 in a 380 card), so the body's inset
 * from the card edge is 12 — the same as the footer, and 12 LESS than the
 * title's 24. This was 16, which put the body further in than the title and
 * inverted that relationship.
 */
export const DEFAULT_PADDING_HORIZONTAL = 12;
/**
 * Top padding of the sheet body when `padding` is `'default'`.
 *
 * Zero: the card's own column `gap-[12px]` IS the 12px gap before the content
 * region — with a title header and without one alike. Any body padding here
 * would stack on top of that gap and break the "always 12px before the
 * content" rule.
 */
export const DEFAULT_PADDING_TOP = 0;
/** Footer height assumed for scroll clearance before the footer has measured. */
export const MIN_FOOTER_HEIGHT = 72;
/** Extra breathing room between the scroll body and a sticky footer. */
export const FOOTER_SCROLL_CLEARANCE_GAP = 28;
/**
 * The detached card's gap to the screen's BOTTOM edge — "even across all sides"
 * (design review 2026-09): the same {@link BOTTOM_SHEET_SCREEN_INSET_PX} as the
 * left and right gaps.
 *
 * - iOS: the card floats 8pt off the glass and the home indicator draws over
 *   its bottom padding, the way iOS 26 system sheets do. The indicator is a
 *   non-interactive overlay (taps inside its zone reach the app; only an
 *   upward swipe from the very edge goes to the system), so nothing is lost.
 * - Android: 8 ABOVE the system navigation area. The 3-button bar takes taps,
 *   so a card 8dp off the physical edge would put its CTA under Back/Home;
 *   the app never learns the navigation mode, only the inset.
 *
 * No inset at all (iPhone SE, iPads without a home indicator) → 8 either way.
 */
export function resolveDetachedBottomInset(
  insetsBottom: number,
  platformOS: string,
): number {
  return platformOS === 'ios'
    ? BOTTOM_SHEET_SCREEN_INSET_PX
    : insetsBottom + BOTTOM_SHEET_SCREEN_INSET_PX;
}

/**
 * Floor for an ATTACHED card's bottom clearance on a device with no inset
 * (iPads without a home indicator, most Android), so its last row never sits
 * flush against the screen edge.
 */
export const MIN_SAFE_AREA_FLOOR = 24;

/** Padding block for the sheet body. `paddingBottom` is resolved separately. */
export interface BottomSheetPaddingStyle {
  paddingHorizontal?: number;
  paddingTop?: number;
  paddingBottom: number;
}

/**
 * Resolve `padding` into a concrete style block. `'none'` keeps only the
 * bottom clearance (full-bleed bodies — PDF, WebView, image viewers — own
 * their own insets but still must clear the footer / home indicator).
 */
export function computePadding(
  padding: BottomSheetPadding,
  paddingBottom: number,
): BottomSheetPaddingStyle {
  if (padding === 'none') return { paddingBottom };
  if (padding === 'default') {
    return {
      paddingHorizontal: DEFAULT_PADDING_HORIZONTAL,
      paddingTop: DEFAULT_PADDING_TOP,
      paddingBottom,
    };
  }
  return {
    paddingHorizontal: padding.horizontal ?? DEFAULT_PADDING_HORIZONTAL,
    paddingTop: padding.top ?? DEFAULT_PADDING_TOP,
    paddingBottom: padding.bottom ?? paddingBottom,
  };
}

/**
 * The media slot's own internal inset. Mirrors `p-[12px]` on
 * `bottomSheetMediaSlotClass` — asserted against it in the content spec.
 */
export const MEDIA_SLOT_PADDING = 12;

/**
 * Whether the SHELL wraps the body in its own scroller.
 *
 * `'auto'` means "scroll whenever the sheet is — or can become —
 * height-bounded": fixed sizes, `expandable` sheets, and content-sized sheets
 * whose content overflowed the cap. Scrollable is the DS DEFAULT; only a
 * truly hugging sheet skips the scroller (it is exactly as tall as its body,
 * so one could never move).
 *
 * `'list'`, `'self'` and `false` all keep the shell's scroller away — the body
 * brings a sheet-aware scroller (`'list'`), owns the drag outright (`'self'`),
 * or holds nothing that wants one (`false`). They differ in what they do to the
 * sheet's own pan; see {@link resolveContentPanning}.
 *
 * `bounded` is the caller's "is/can be height-bounded" — the shells pass
 * `fixedSize || cappedAuto || expandable`.
 */
export function shouldScroll(scroll: BottomSheetScroll, bounded: boolean): boolean {
  if (typeof scroll === 'boolean') return scroll;
  if (scroll === 'self' || scroll === 'list') return false;
  return bounded;
}

/**
 * `scroll="self"` — the BODY owns the vertical drag OUTRIGHT, because it holds
 * a view with gestures of its own: a WebView, a PDF, a pinch-zoom image, a
 * drawing canvas, or a plain (sheet-UNAWARE) `ScrollView`/`FlatList`.
 *
 * Nothing arbitrates between such a body and the sheet, so the sheet must stand
 * its pan down completely or it wins the drag the child never gets to claim.
 * That is the half `scroll={false}` could never express: `false` is read as
 * "nothing in here wants vertical drags", which is a MENU, and a menu wants the
 * drag-anywhere pan.
 *
 * NOT `'list'`. A gorhom `BottomSheetFlatList` arbitrates with the sheet by
 * design, and needs the sheet's pan LEFT ON to do it — see
 * {@link resolveContentPanning}.
 */
export function bodyOwnsVerticalDrag(scroll: BottomSheetScroll): boolean {
  return scroll === 'self';
}

/**
 * Whether a drag on the BODY pans the sheet — gorhom's
 * `enableContentPanningGesture`, as the DS decides it.
 *
 * The rule in one line: the sheet keeps the drag unless something inside the
 * card takes it away — the shell's scroller, or a body that owns the drag
 * outright under `scroll="self"`.
 *
 * `'list'` is the case that keeps it. A gorhom `BottomSheetFlatList` does not
 * take the drag away from the sheet, it ARBITRATES for it: while the list can
 * scroll, the drag scrolls it; at content offset 0 a downward drag hands back
 * to the sheet and closes it. gorhom needs the content pan ON to do that —
 * with it off, `useScrollable` pins the scrollable UNLOCKED forever and the
 * hand-off never happens, so the sheet cannot be closed by dragging the
 * conversation down from its top.
 *
 * The shell's OWN `BottomSheetScrollView` is the same gorhom component and
 * wants the same treatment — but only once the sheet has a SINGLE detent.
 * gorhom's arbitration locks a scrollable below the top detent (drag first
 * expands the sheet, and only at the top does the list take over), which on a
 * multi-detent sheet reads as "the list is frozen". With one detent there is
 * no "below the top" to be locked at, so the pan can stay on and the library
 * runs its own hand-off: the body scrolls while it can, and a downward drag at
 * offset 0 closes the card. That is what `singleDetent` selects.
 *
 * THIS FUNCTION IS THE FIX, so it lives here rather than inline in the shell:
 * `.native.tsx` is excluded from shared's typecheck and carries no unit tests
 * (see this module's header), and the expression that shipped the Ka mini-chat
 * bug — a body that scrolled itself while the sheet still believed it was a
 * menu — is exactly the one that must not be able to regress unnoticed. Pinned
 * in `BottomSheet.layout.spec.ts`.
 *
 * `explicit` is the caller's raw `enableContentPanningGesture`; it always wins.
 * `singleDetent` says the sheet rests at exactly one position — see above.
 */
export function resolveContentPanning(
  explicit: boolean | undefined,
  scroll: BottomSheetScroll,
  bounded: boolean,
  singleDetent = false,
): boolean {
  if (explicit !== undefined) return explicit;
  // Nothing arbitrates for a body that owns the drag outright.
  if (bodyOwnsVerticalDrag(scroll)) return false;
  // A body's own gorhom list arbitrates the SAME way the shell's scroller does,
  // so it gets the same one-detent rule. On a multi-detent (`expandable`) sheet
  // gorhom LOCKS a scrollable below the top detent, so a live pan froze the
  // list: "Add existing sets" stopped scrolling with rows still under its
  // "Add N sets" button (designer, 2026-10-01). Pan off there pins the list
  // unlocked — it scrolls at every height — and the handle moves the sheet.
  if (scroll === 'list') return singleDetent;
  // The shell's gorhom scroller arbitrates too, once there is one detent.
  if (shouldScroll(scroll, bounded)) return singleDetent;
  return true;
}

/** A sheet is "fixed size" for every `size` except content-driven `'auto'`. */
export type BottomSheetKeyboardModes = Pick<
  BottomSheetProps,
  'keyboardApproach' | 'keyboardBehavior' | 'keyboardBlurBehavior'
>;

/**
 * The DS keyboard approaches as gorhom's raw knobs. Explicit raw props win —
 * they are the escape hatch.
 *
 *   'float-footer' → behavior 'extend'       the sheet holds its detent (a
 *                                            single-detent sheet doesn't move)
 *                                            and the footer rides the keyboard.
 *   'shift-sheet'  → behavior 'interactive'  the whole sheet lifts above the
 *                    + blur 'restore'        keyboard, footer kept in flow.
 *
 * `'restore'` is what brings a shifted sheet back DOWN: gorhom's default blur
 * behaviour (`'none'`) drops the keyboard-hidden event on the floor, so an
 * `interactive` sheet stays parked at its lifted position — a card floating
 * mid-screen the moment a blur or a stacked sheet takes the keyboard away
 * (the todo form under its Repeat picker, 2026-08-27).
 *
 * Which is why `'restore'` is paired with `interactive` WHEREVER that
 * behaviour comes from, not only from `'shift-sheet'`. gorhom's own defaults
 * are `interactive` + `'none'` — a sheet that lifts and never comes back — so
 * every sheet that sets NO keyboard props at all inherits the broken pair the
 * moment one of its fields registers as the keyboard target. That is not a
 * hypothetical: it is what a plain DS `Input` becoming a `SheetKeyboardInput`
 * does. The two knobs are only separable for behaviours that don't move the
 * card (`extend`, `fillParent`), so an explicit `keyboardBlurBehavior` still
 * wins — the escape hatch is intact, it just isn't the default.
 */
export function resolveKeyboardModes({
  keyboardApproach,
  keyboardBehavior,
  keyboardBlurBehavior,
}: BottomSheetKeyboardModes): Pick<
  BottomSheetKeyboardModes,
  'keyboardBehavior' | 'keyboardBlurBehavior'
> {
  const resolvedBehavior =
    keyboardBehavior ??
    (keyboardApproach === 'float-footer'
      ? 'extend'
      : keyboardApproach === 'shift-sheet'
        ? 'interactive'
        : undefined);

  return {
    keyboardBehavior: resolvedBehavior,
    keyboardBlurBehavior:
      keyboardBlurBehavior ??
      // `undefined` is gorhom's `interactive` default, so it needs the pairing
      // just as much as an explicit one.
      (resolvedBehavior === 'interactive' || resolvedBehavior === undefined
        ? 'restore'
        : undefined),
  };
}

export function isFixedSize(size: BottomSheetSize): boolean {
  return size !== 'auto';
}

/**
 * gorhom's `android_keyboardInputMode` — deliberately `undefined`, i.e.
 * gorhom's own `'adjustPan'` default, on EVERY sheet.
 *
 * That prop is not a setting: it is a DECLARATION about the host Activity.
 * `'adjustResize'` tells gorhom "the OS resizes my window for the keyboard,
 * stand down", and gorhom then zeroes its `heightWithinContainer` and skips
 * repositioning entirely (`BottomSheet.tsx`, the
 * `KEYBOARD_INPUT_MODE.adjustResize` branch).
 *
 * flashka-app runs EDGE-TO-EDGE (`react-native-edge-to-edge`, `targetSdk`
 * 36), and edge-to-edge is exactly the mode in which Android stops honouring
 * `android:windowSoftInputMode="adjustResize"`: the IME arrives as a window
 * inset and the window keeps its full height. So the declaration was false —
 * gorhom stood down, nothing else resized, and a fixed-size sheet simply sat
 * under the keyboard (mini-chat on Android: the composer was covered, the
 * card never shrank). Content-sized sheets never claimed it and moved
 * correctly, which is what made the bug look size-specific.
 *
 * Leaving it unset puts Android on the same path iOS has always taken: gorhom
 * measures the keyboard itself and drives `keyboardBehavior`. Named, rather
 * than left off at the call site, so the reasoning has somewhere to live and a
 * spec can pin it — re-adding `'adjustResize'` should fail a test, not a
 * device.
 *
 * It also retires the Android footer's `KeyboardStickyView` wrapper: gorhom's
 * `BottomSheetFooterContainer` lifts the footer slot by the keyboard height
 * itself now, so the stand-in that used to supply that lift would double it.
 */
export const ANDROID_KEYBOARD_INPUT_MODE: 'adjustPan' | 'adjustResize' | undefined =
  undefined;

/**
 * gorhom snap points for a `size`. `undefined` means "no snap points" — the
 * caller pairs that with `enableDynamicSizing` so the sheet hugs its content.
 *
 * `containerHeight` is gorhom's HOSTING container — the window minus the top
 * AND bottom insets. `'full'` is exactly that, and every detent is capped by
 * it: gorhom clamps an oversize detent's position, not its layout, so the
 * content would otherwise run past the card's bottom edge.
 *
 * `expandable` appends a full-height snap so `ref.expand()` / a drag upward
 * reaches the top of the screen and `ref.collapse()` returns to `size`.
 */
export function resolveSnapPoints(
  size: BottomSheetSize,
  windowHeight: number,
  containerHeight: number,
  expandable: boolean,
): number[] | undefined {
  const full = containerHeight;
  const base =
    size === 'auto'
      ? undefined
      : size === 'full'
        ? full
        : size === 'half'
          ? windowHeight * 0.5
          : size;

  if (base === undefined) return expandable ? [full] : undefined;
  if (!expandable || base >= full) return [Math.min(base, full)];
  return [base, full];
}

export interface BottomSheetSpacingInput {
  /**
   * The card sits ON the screen edges (`attached`), so gorhom's `bottomInset`
   * is 0 and the body — not the modal — owes the home indicator its clearance.
   */
  attached?: boolean;
  /** Only read for an `attached` card — a detached one never pays the safe area here. */
  insetsBottom?: number;
  /** Only read for an `attached` card: the keyboard covers the home indicator. */
  isKeyboardVisible?: boolean;
  hasFooter: boolean;
  scrollEnabled: boolean;
  /** Measured footer content height, without the bottom inset. 0 until measured. */
  footerContentHeight: number;
  /** An explicit `padding={{ bottom }}` from the caller wins verbatim. */
  userPaddingBottom?: number;
}

export interface BottomSheetSpacing {
  /** `paddingBottom` for the scroll contentContainer / static body. */
  contentPaddingBottom: number;
  /** Footer height used for clearance before the real one is measured. */
  footerMinHeightForPadding: number;
  /** Whether gorhom should auto-adjust scroll content for the footer. */
  gorhomFooterMarginAdjustment: boolean;
}

/**
 * Single source of truth for the body's bottom spacing.
 *
 * The safe area is NOT paid here any more: the detached card's `bottomInset`
 * ({@link resolveDetachedBottomInset}) already places the whole card, and a
 * hoisted footer sits on the card's bottom edge with the same 12px inner
 * padding as every other side. (It used to add `max(inset, 24)` under the
 * footer a second time — the "bottom padding too high" the designer flagged.)
 */
export function resolveBottomSheetSpacing(
  input: BottomSheetSpacingInput,
): BottomSheetSpacing {
  const {
    attached = false,
    insetsBottom = 0,
    isKeyboardVisible = false,
    hasFooter,
    scrollEnabled,
    footerContentHeight,
    userPaddingBottom,
  } = input;

  const effectiveSafeBottom = isKeyboardVisible
    ? 0
    : Math.max(insetsBottom, MIN_SAFE_AREA_FLOOR);

  const gorhomFooterMarginAdjustment = hasFooter && scrollEnabled;

  const footerMinHeightForPadding = hasFooter
    ? footerContentHeight > 0
      ? footerContentHeight
      : MIN_FOOTER_HEIGHT
    : 0;

  let contentPaddingBottom: number;
  if (userPaddingBottom !== undefined) {
    contentPaddingBottom = userPaddingBottom;
  } else if (hasFooter) {
    contentPaddingBottom = gorhomFooterMarginAdjustment
      ? FOOTER_SCROLL_CLEARANCE_GAP
      : footerMinHeightForPadding + FOOTER_SCROLL_CLEARANCE_GAP;
  } else {
    // Zero, NOT the safe area: the DS card is DETACHED — the modal's
    // `bottomInset` already floats it clear of the home indicator, so padding
    // the body for it again was double-dipping. The card's own `pb-[12px]`
    // provides the "always 12px after the content" gap.
    //
    // ATTACHED is the one case where that inset does not exist: the card runs
    // to the screen edge, so the body carries the safe area itself or the last
    // row sits under the home indicator.
    contentPaddingBottom = attached ? effectiveSafeBottom : 0;
  }

  return {
    contentPaddingBottom,
    footerMinHeightForPadding,
    gorhomFooterMarginAdjustment,
  };
}

/**
 * Native sheets never draw a ✕ (design review, 2026-09): on a phone a sheet is
 * dismissed by the gesture it was opened with — drag the pill down, tap the
 * backdrop, or the Android back button — and a ✕ on top of that read as
 * clutter. `closable` and `<BottomSheetClose>` therefore only render on the web
 * shell; the native shell's hosted Surface drops them. Every native sheet still
 * has a way out that is not the ✕: the pill is an accessible "Close" button,
 * VoiceOver's escape gesture dismisses, and the Android back button closes the
 * topmost dismissable sheet (`BottomSheet.native.tsx`).
 */
export const NATIVE_SHEET_SHOWS_CLOSE = false;
/**
 * `pointerEvents` for the view the native shell wraps gorhom's backdrop in:
 * `'none'` once a close is under way, `'box-none'` otherwise.
 *
 * gorhom's `BottomSheetBackdrop` keeps catching touches until the sheet is
 * FULLY closed (`animatedIndex <= disappearsOnIndex`, flipped through a
 * `runOnJS` hop). Under Reanimated 4 the iOS close spring settles ~0.7s after
 * the card looks gone, and then the modal still has to unmount — so for that
 * whole window an all-but-invisible backdrop swallowed the tap meant for the
 * screen ("close the sheet, tap Sign in again, it lags"). Unfixed upstream
 * through 5.2.14 (gorhom#2764 / #2680).
 *
 * `'none'` takes the wrapper AND the backdrop inside it out of hit-testing, so
 * the tap reaches the screen below; `'box-none'` means the wrapper itself
 * never catches and the backdrop keeps its own behaviour. Not gorhom's own
 * knobs: `disappearsOnIndex` also reshapes the dim's opacity curve, and
 * `enableTouchThrough` only seeds the backdrop's initial state.
 */
export function backdropPointerEvents(closing: boolean): 'none' | 'box-none' {
  return closing ? 'none' : 'box-none';
}

/**
 * gorhom's `ANIMATION_STATUS.RUNNING` (`src/constants.ts`: UNDETERMINED 0,
 * RUNNING 1, STOPPED 2, INTERRUPTED 3). Mirrored as a number, not imported:
 * this module is also the WEB shell's, and must not pull gorhom in. Pinned
 * against the installed gorhom in flashka-app's
 * `__tests__/shared-ui/bottom-sheet-gorhom-contract.spec.ts` — the app is
 * where gorhom is installed and bumped.
 */
export const GORHOM_ANIMATION_STATUS_RUNNING = 1;

/**
 * Whether gorhom's animation state is a CLOSE in progress — the one source of
 * the native shell's `closing` flag ({@link backdropPointerEvents},
 * {@link resolvePresentAction}).
 *
 * Read off the state every move writes, not off `onAnimate`: gorhom skips
 * that callback when the target is the index the sheet already has — and the
 * current index only updates when a move COMPLETES. So a sheet the user
 * caught mid-close (a pan grab cancels the animation) and let go back at its
 * detent announced nothing, and the flag stayed stuck on over an open sheet
 * whose backdrop then passed taps through. The state has no such blind spot:
 * a close is RUNNING toward -1; a grab (`stopAnimation`) and every completion
 * leave it STOPPED; a snap back — or our own reverse — is RUNNING toward a
 * detent.
 *
 * A worklet: the shell evaluates it on the UI thread.
 */
export function isClosingAnimation(state: {
  status?: number;
  nextIndex?: number;
}): boolean {
  'worklet';
  return state.status === GORHOM_ANIMATION_STATUS_RUNNING && state.nextIndex === -1;
}

/**
 * What the native shell's `present()` does, given the close (if any) the
 * sheet is in the middle of.
 *
 * `'reverse'` — a close the USER started (backdrop press, pan release) that is
 * still animating. gorhom 5.2.9 LOSES a `present()` on a modal that is still
 * mounted: the sheet carries on to -1 and unmounts. So the shell turns the
 * close around with `snapToIndex` instead — what upstream shipped in 5.2.10
 * (gorhom#2655). `snapToIndex` retargets a regular close.
 *
 * `'present'` — everything else, including a close WE started with
 * `dismiss()`. That one is gorhom's `forceClose`, which blocks `snapToIndex`
 * (`isForcedClosing`), so it cannot be reversed; it runs to the end and the
 * present is lost, exactly as before.
 */
export function resolvePresentAction({
  closing,
  dismissRequested,
}: {
  /** A close is animating — see {@link isClosingAnimation}. */
  closing: boolean;
  /** That close is ours: the shell called `dismiss()` (a `forceClose`). */
  dismissRequested: boolean;
}): 'present' | 'reverse' {
  return closing && !dismissRequested ? 'reverse' : 'present';
}

/**
 * Whether the native shell's dismiss may reach gorhom: only for a sheet it
 * has actually presented.
 *
 * The controlled-`open` effect runs on MOUNT, so every sheet that starts with
 * `open={false}` asks to dismiss before it was ever presented. 5.2.9 shrugs
 * that off; 5.2.11+ does not (gorhom#2669) — a `dismiss()` before the first
 * `present()` makes every later `present()` a no-op, i.e. a sheet that can
 * never open. This guard is what keeps a gorhom bump from bricking every sheet
 * in the app.
 */
export function shouldDismiss(presented: boolean): boolean {
  return presented;
}

/** What the dismiss key (Android Back / web `Escape`) does to the TOPMOST sheet. */
export type DismissKeyAction = 'step-back' | 'close' | 'hold';

/**
 * The dismiss key behaves like the sheet's own chrome, never like the screen
 * behind it — the press is ALWAYS consumed while a sheet is open, so it can
 * never fall through and pop the screen under a sheet the user is looking at.
 *
 * - `leaving` — a close is already sliding away → `'hold'`: let it finish.
 * - `canStepBack` — the header shows its back arrow (a later step of a
 *   multi-step sheet) → `'step-back'`, exactly what the arrow does.
 * - otherwise `'close'`, unless the sheet opted out of a casual dismiss
 *   (`enablePanDownToClose={false}` / `dismissOnBackdropPress={false}` — the
 *   time's-up quiz sheet, a plan being generated, a pending sign-in code) →
 *   `'hold'`: those sheets wait for one of their own buttons. Android treats a
 *   non-cancelable dialog the same way.
 */
export function resolveDismissKeyAction({
  leaving,
  canStepBack,
  enablePanDownToClose,
  dismissOnBackdropPress,
}: {
  leaving: boolean;
  canStepBack: boolean;
  enablePanDownToClose: boolean;
  dismissOnBackdropPress: boolean;
}): DismissKeyAction {
  if (leaving) return 'hold';
  if (canStepBack) return 'step-back';
  return enablePanDownToClose && dismissOnBackdropPress ? 'close' : 'hold';
}

/**
 * The press the header's back ARROW makes, or `undefined` when there is no
 * arrow — the dismiss key's `'step-back'` target. A `<BottomSheetBack>` slot
 * wins over `onBack` (as it does in the Surface); a slot with custom
 * `children` is a leading affordance that isn't "go back" (a filter reset, an
 * avatar), so the key never presses it.
 */
export function resolveBackArrowPress<P extends (...args: never[]) => void>(
  backSlot: { onPress?: P; children?: unknown } | undefined,
  onBack: P | undefined,
): P | undefined {
  if (backSlot) return backSlot.children == null ? backSlot.onPress : undefined;
  return onBack;
}
